import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const BOOK_COVER =
  "https://customer-assets.emergentagent.com/job_growth-journey-146/artifacts/chjevp64_FF9B91FB-B06E-4F28-81AD-9DBE4D3D8B14.jpeg";

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative hero-glow pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
    >
      <div className="grain-overlay absolute inset-0 pointer-events-none" />

      {/* Decorative gold ring */}
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border border-[#d4af37]/15 pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full border border-[#d4af37]/10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left: copy */}
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/5 text-[11px] uppercase tracking-[0.25em] text-[#e5c158]">
            <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-pulse" />
            New release · 2026 edition
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-display mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white"
          >
            Become <span className="gold-text italic">3x</span> better
            <br />
            in 30 unforgiving days.
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg text-neutral-400 leading-relaxed">
            A no-fluff playbook of daily disciplines, rituals and mindset
            shifts. One chapter a day. Thirty days. The compounding result is
            not subtle — it&apos;s the new norm.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              asChild
              data-testid="hero-buy-btn"
              className="bg-[#d4af37] text-black hover:bg-[#e5c158] rounded-full h-12 px-7 text-sm font-medium shadow-[0_0_50px_rgba(212,175,55,0.25)] transition-all hover:translate-y-[-1px]"
            >
              <a
                href="https://kcuttstudios.gumroad.com/l/ijgso"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                Get the ebook — $16.99
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>

            <Button
              asChild
              variant="ghost"
              data-testid="hero-preview-btn"
              className="text-neutral-200 hover:text-white hover:bg-white/5 rounded-full h-12 px-6 text-sm border border-white/10"
            >
              <a href="#journey">Preview the 30 days</a>
            </Button>
          </div>

          {/* Social proof row */}
          <div className="mt-10 flex items-center gap-6">
            <div className="flex -space-x-2">
              {[
                "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=80&q=80",
                "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&w=80&q=80",
                "https://images.unsplash.com/photo-1627161683077-e34782c24d81?crop=entropy&cs=srgb&fm=jpg&w=80&q=80",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-9 h-9 rounded-full border-2 border-[#0a0a0a] object-cover"
                />
              ))}
            </div>
            <div className="text-xs text-neutral-400">
              <div className="flex items-center gap-1 text-[#e5c158]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-current" />
                ))}
              </div>
              <span className="text-neutral-500">
                Trusted by 12,000+ readers in 47 countries
              </span>
            </div>
          </div>
        </div>

        {/* Right: book cover */}
        <div className="relative flex justify-center lg:justify-end fade-up [animation-delay:200ms]">
          <div className="relative perspective-[1200px]">
            {/* gold glow ring */}
            <div className="absolute -inset-10 rounded-full bg-[#d4af37]/15 blur-3xl" />
            <img
              src={BOOK_COVER}
              alt="30 Days of Growth — by Kendrick Callicutt"
              data-testid="hero-book-cover"
              className="book-float relative max-w-[420px] md:max-w-[480px] w-full drop-shadow-[0_50px_60px_rgba(0,0,0,0.7)]"
            />
            {/* reflection */}
            <div
              className="absolute left-1/2 -translate-x-1/2 bottom-[-30px] w-[60%] h-8 rounded-[50%] bg-[#d4af37]/10 blur-2xl"
              aria-hidden
            />
          </div>
        </div>
      </div>
    </section>
  );
}
